import Layout from '../../../components/UI/Layout';
import BreadcrumbNav from 'components/UI/BreadcrumbNav';
import type { ContentType } from 'contentful';

import cmsClient from '../../../lib/cms/client';
import { resolveBreadcrumbSlugs } from '../../../lib/cms/breadcrumbs';
import type { BreadcrumbItem } from '../../../lib/cms/breadcrumbs';
import hydrateTemplate from '../../../lib/cms/links';
import {
  BrandedSiteProps,
  getServerSidePropsWithErrors,
  getContentfulPageData,
  toSlug,
  cacheDirective,
  PageDates,
  getContentfulSiteData,
  isInvalid,
  withSiteData,
} from '../../../lib/routing';
import { ReactElement } from 'react';
import { IPageFields } from 'types/contentful';
import { JsonObject } from 'lib/json';
import {
  buildDynamicComponent,
  DynamicComponentTypes,
  PreparedComponentObject,
  otherParams,
  translateServerSideComponent,
} from 'components';
import { getStateFromCode } from 'lib/util';

type SluggedProps<K extends DynamicComponentTypes = DynamicComponentTypes, CP = unknown> = {
  preview: boolean;
  content: PreparedComponentObject<K, CP>[];
  siteData: BrandedSiteProps;
  currentSlug: string;
  title: string;
  description: string;
  noIndexNoFollow: boolean;
  pageDates?: PageDates;
  hideTopNavCtas?: boolean;
  stateCode: string;
  cityCode: string;
  breadcrumbs: BreadcrumbItem[];
  jsonSchema?: JsonObject;
};

// Helper function to format city name (capitalize first letter of each word)
const formatCityName = (city: string): string => {
  return city
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export default function Slugged<K extends DynamicComponentTypes, CP>({
  preview,
  title,
  description,
  currentSlug,
  content,
  siteData,
  noIndexNoFollow,
  pageDates,
  hideTopNavCtas,
  cityCode,
  breadcrumbs,
  jsonSchema,
}: SluggedProps<K, CP>): ReactElement {
  const formattedCity = formatCityName(cityCode);

  const hubObj = {
    type: 'city',
    value: cityCode,
    label: formattedCity,
  };
  const components = content.map((comp: PreparedComponentObject<K, CP>, index: number) => {
    const dynamicComp = buildDynamicComponent(comp, siteData.siteName, preview, { hub: hubObj });
    if (dynamicComp === undefined) return;

    return <div key={index}>{dynamicComp}</div>;
  });

  const nextSeoProps = {
    description,
    title,
  };

  return (
    <Layout
      noIndexNoFollow={noIndexNoFollow}
      siteData={siteData}
      preview={preview}
      currentSlug={currentSlug}
      nextSeoProps={nextSeoProps}
      pageDates={pageDates}
      hideTopNavCtas={hideTopNavCtas}
      jsonSchema={jsonSchema}
    >
      <BreadcrumbNav items={breadcrumbs} />
      <div>{components}</div>
    </Layout>
  );
}

type ToRecord<T> = Record<keyof T, T[keyof T]>;

export const getServerSideProps = getServerSidePropsWithErrors<SluggedProps>(
  async ({ resolvedUrl, params, req, res, preview, query }) => {
    const stateCode = params?.state as string;
    const cityCode = params?.city as string;

    if (!stateCode || !cityCode) {
      return {
        notFound: true,
      };
    }

    // Build slug: /locations/al/birmingham
    const slug = toSlug(['locations', stateCode.toLowerCase(), cityCode.toLowerCase()]) || '';
    const queryOptions = {
      'fields.site.sys.contentType.sys.id': 'site',
      'fields.site.fields.id': req.headers.host,
      'fields.slug': slug,
      content_type: 'page',
      include: 10, // Make sure we retrieve nav with links
    };

    const siteData = await getContentfulSiteData({ req, res, resolvedUrl, preview });

    if (isInvalid(siteData)) {
      return siteData;
    }

    const contentfulData = await getContentfulPageData<IPageFields>({
      res,
      req,
      preview,
      queryOptions,
    });

    if (isInvalid(contentfulData)) {
      return withSiteData(contentfulData, siteData);
    }

    const resolvedBreadcrumbs = await resolveBreadcrumbSlugs({
      breadcrumbs: [
        { slug: '/locations', label: 'Locations' },
        {
          slug: `/locations/${stateCode.toLowerCase()}`,
          label: getStateFromCode(stateCode),
        },
        {
          slug: `/locations/${stateCode.toLowerCase()}/${cityCode.toLowerCase()}`,
          label: formatCityName(cityCode),
        },
      ],
      siteHost: req.headers.host || '',
      preview: !!preview,
    });

    const client = cmsClient(preview);

    const pageMeta = contentfulData.item.fields;

    // fetch template
    const rawTemplate: ContentType = await client.getContentType(
      pageMeta.template.sys.contentType.sys.id
    );

    // remove symbol fields
    const templateLayout = rawTemplate.fields.filter((field) => field.type !== 'Symbol');

    const searchspringSiteId = siteData?.ecommIds?.fields?.searchSpringId || '';
    query.siteId = searchspringSiteId;
    const freshPaintToken = siteData?.freshPaintToken || '';
    const factua = siteData?.factua || false;

    const otherParams: otherParams = {
      query,
      req,
      res,
      resolvedUrl,
      searchspringSiteId,
      freshPaintToken,
      factua,
    };
    // hydrate and build out template requirements
    const hydrated = await Promise.all(
      hydrateTemplate(
        templateLayout,
        pageMeta.template.fields as ToRecord<typeof pageMeta.template.fields>,
        contentfulData.includes?.Asset || [],
        contentfulData.includes?.Entry || []
      ).map((item) => translateServerSideComponent(item, !!preview, otherParams))
    );
    const formattedCity = formatCityName(cityCode);
    const cityLocations = await client.getEntries<IPageFields>({
      'fields.site.sys.contentType.sys.id': 'site',
      'fields.site.fields.id': req.headers.host,
      'fields.state': stateCode.toUpperCase(),
      'fields.city': formattedCity,
      content_type: 'pageLocation',
      limit: 1,
      select: 'sys.id',
    });
    const locationCount = cityLocations.total;
    const brandName = siteData.siteName;
    const replaceSeoTokens = (value = ''): string =>
      value
        .replace(/{{\s*location\s*}}/gi, formattedCity)
        .replace(/{{\s*brand\s*}}/gi, brandName)
        .replace(/{{\s*locationCount\s*}}/gi, String(locationCount));
    const seoTitle = replaceSeoTokens(pageMeta.title || '');
    const seoDescription = replaceSeoTokens(pageMeta.description || '');

    res.setHeader('Cache-Control', cacheDirective(preview));

    return {
      props: {
        noIndexNoFollow: !!pageMeta.doNotIndex,
        preview: !!preview,
        siteData,
        title: seoTitle,
        description: seoDescription,
        content: hydrated,
        currentSlug: slug,
        stateCode: stateCode.toLowerCase(),
        cityCode: cityCode.toLowerCase(),
        pageDates: {
          datePublished: contentfulData.item.sys.createdAt,
          dateModified: contentfulData.item.sys.updatedAt,
        },
        hideTopNavCtas: pageMeta.hideTopNavCtas || false,
        breadcrumbs: resolvedBreadcrumbs,
        ...(pageMeta.jsonSchema ? { jsonSchema: pageMeta.jsonSchema as JsonObject } : {}),
      },
    };
  }
);
